// src/views/EmployeeView.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

export default function EmployeeView({
  sessions,
  user,
  onRegister,
  onUnregister,
}) {
  const [registeredIds, setRegisteredIds] = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const notifyTimer = useRef(null);

  const notify = (message, type = "success") => {
    setNotification({ message, type });
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(() => setNotification(null), 3500);
  };

  useEffect(() => () => clearTimeout(notifyTimer.current), []);

  useEffect(() => {
    if (!user?.uid || sessions.length === 0) return;

    // Alleen de eigen inschrijving uitlezen — de deelnemerslijst van een sessie
    // is voorbehouden aan trainer/admin.
    const unsubs = sessions.map((s) =>
      onSnapshot(doc(db, "sessions", s.id, "attendees", user.uid), (snap) => {
        setRegisteredIds((prev) => {
          const next = new Set(prev);
          snap.exists() ? next.add(s.id) : next.delete(s.id);
          return next;
        });
      }),
    );

    return () => unsubs.forEach((u) => u());
  }, [sessions, user?.uid]);

  const visibleSessions = sessions
    .filter((s) => {
      if (!user?.email) return false;
      if (s.date && s.time) {
        const [h, m] = s.time.split(":").map(Number);
        const end = new Date(s.date);
        end.setHours(h, m + Number(s.duration || 0), 0, 0);
        if (end < new Date()) return false;
      }
      // Wie welke sessie mag zien wordt afgedwongen door de Firestore-rules;
      // App.js vraagt alleen sessies op waar deze gebruiker recht op heeft.
      // Hier blijft alleen de presentatiefilter over.
      return s.status === "ready";
    })
    .sort(
      (a, b) =>
        new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`),
    );

  const handleToggle = async (s) => {
    setLoadingIds((prev) => new Set(prev).add(s.id));
    try {
      if (registeredIds.has(s.id)) {
        await onUnregister(s);
        notify("Je bent afgemeld.", "info");
      } else {
        await onRegister(s);
        notify(
          "Ingeschreven! Je ontvangt een Google Calendar uitnodiging.",
          "success",
        );
      }
    } catch {
      notify("Er ging iets mis. Probeer opnieuw.", "error");
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  return (
    <div className="employee-view">
      {notification && (
        <div className={`notify-bar notify-${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="employee-header">
        <h2>Beschikbare trainingen</h2>
        <p>
          Schrijf je in voor een sessie en ontvang een Google Calendar
          uitnodiging.
        </p>
      </div>

      {visibleSessions.length === 0 && (
        <div className="empty-state-box">
          <p>Geen trainingen beschikbaar op dit moment.</p>
        </div>
      )}

      <div className="sessions-list">
        {visibleSessions.map((s) => {
          const isReg = registeredIds.has(s.id);
          const isLoading = loadingIds.has(s.id);

          return (
            <div
              key={s.id}
              className={`card session-card ${isReg ? "session-card-registered" : ""}`}
            >
              {isReg && (
                <div className="registered-banner">
                  <span className="registered-check">✓</span>
                  <span>Je bent ingeschreven</span>
                  {s.meetLink && (
                    <a
                      href={s.meetLink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-meet-banner"
                    >
                      Deelnemen
                    </a>
                  )}
                  <button
                    className="btn-unregister-banner"
                    onClick={() => handleToggle(s)}
                    disabled={isLoading}
                  >
                    {isLoading ? "…" : "Afmelden"}
                  </button>
                </div>
              )}
              <div className="session-card-top">
                <div>
                  <h3 className="session-name">{s.trainingName}</h3>
                  <p className="session-meta">
                    {formatDate(s.date)} · {formatTime(s.time, s.duration)} · {s.trainerName}
                  </p>
                </div>
                {!isReg && (
                  <button
                    className="btn-register"
                    onClick={() => handleToggle(s)}
                    disabled={isLoading}
                  >
                    {isLoading ? "…" : "Inschrijven"}
                  </button>
                )}
              </div>
              {s.description && <p className="tile-desc">{s.description}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  const formattedDate = new Date(y, m - 1, d).toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
}

function formatTime(time, duration) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const endMinutes = h * 60 + m + Number(duration || 0);
  const eh = Math.floor(endMinutes / 60) % 24;
  const em = endMinutes % 60;
  const end = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
  return `${time} – ${end}`;
}
